import { useMemo } from 'react';

import {
  Box,
  Card,
  CardContent,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import { SavedRouteAlertEvent } from 'state/emailAlertsSlices';
import { useAppSelector } from 'state/hooks';
import { selectCurrentProjectInfo } from 'state/workflowSlice';

import dayjs from 'dayjs';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { SegmentSvg } from 'features/common/SegmentSvg';
import {
  formatDateTimeWithAt,
  nowUnix,
  parseDateTimeIgnoringTz,
} from 'features/common/datetimeUtils';
import {
  classNames,
  formatSpeedKmph,
  formatTraveltime,
} from 'features/common/utils';
import { useMenu } from 'features/workflow_routemonitoring/hooks/useMenu';
import { NA_String } from 'features/workflow_timeline/timelineTooltipContents';
import { dimmed_white_color } from 'theme/cemTheme';

import { MetricType } from '../alertHelpers';
import { useNotificationProject } from '../useNotificationProject';

import styles from './NotificationLog.module.css';
import commonStyles from 'features/common/common.module.css';

const IMG_SMALL_SIZE = 64;
const CHAR_LIMIT = 40;

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 12 * MONTH;

// aproximate time, dayjs.duration assumes month=30 days
const getTimeFormat = (v: number) => {
  if (v >= YEAR) return 'Y [y] M [mo]';
  if (v >= MONTH) return 'M [mo] D [days]';
  // if more than a day, do not show mins and seconds
  if (v >= DAY) return 'D [days]';
  // time is in hours
  if (v >= HOUR) return 'H [hrs] m [mins]';
  return 'm [mins] s [s]';
};

export default function NotificationCard({
  notification,
  wide,
}: {
  wide: boolean;
  notification: SavedRouteAlertEvent;
}) {
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const {
    route,
    route_name,
    metric_value,
    alert,
    created_on,
    ended_on,
    metric_type,
    alert_metric_criteria,
  } = notification;
  const formattedSpeed =
    metric_value?.average_speed_kmph != null
      ? formatSpeedKmph(
          metric_value.average_speed_kmph,
          userProject?.uses_metric,
        )
      : NA_String;

  const formattedDelay = formatTraveltime(
    Math.round(metric_value?.delay_seconds ?? 0),
  );

  const alertType = useMemo(() => {
    if (metric_type === MetricType.PERCENT_SLOWDOWN)
      return `${alert_metric_criteria * 100}% Travel Time Increase`;
    return 'Unknown';
  }, [metric_type, alert_metric_criteria]);

  // update time on open
  const createOnDiff = useMemo(() => {
    if (wide || !open) return undefined;
    const val = Math.floor((nowUnix() - dayjs(created_on).valueOf()) / 1000);
    return formatTraveltime(val, getTimeFormat(val));
  }, [created_on, wide, open]);

  const slowdown =
    metric_value?.percent_slowdown != null
      ? Math.max(0.1, Math.round(metric_value.percent_slowdown * 100))
      : undefined;
  return (
    <Card
      variant="outlined"
      className={classNames(styles.card, !wide && styles.cardSmall)}
    >
      <CardContent className={styles.cardContent}>
        <SegmentSvg
          type="route"
          id={route}
          size={!wide ? IMG_SMALL_SIZE : undefined}
        />
        <Box className={styles.cardTxt}>
          <Box
            className={classNames(
              styles.cardHeader,
              wide && styles.cardHeaderWide,
            )}
          >
            <Typography variant="body2">
              <strong title={route_name}>
                {route_name?.length > CHAR_LIMIT
                  ? `${route_name.slice(0, CHAR_LIMIT)}...`
                  : route_name}
              </strong>{' '}
              experienced a <strong>{formattedDelay} delay</strong> with an{' '}
              <strong>average speed of {formattedSpeed}</strong>
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Box className={styles.typicalCompare}>
                <FontAwesomeIcon
                  size="0.75rem"
                  name="circle-arrow-down"
                  sx={{ color: dimmed_white_color }}
                />
                <Typography variant="body2">
                  <span className={styles.highlightCol}>{slowdown}%</span> than
                  typical {!wide && ` · ${createOnDiff} ago`}
                </Typography>
              </Box>
              {wide ? (
                <NotificationMenu alertId={alert} />
              ) : ended_on ? (
                <StatusIcon resolved withLabel />
              ) : (
                <StatusIcon resolved={false} withLabel />
              )}
            </Box>
          </Box>
          {wide && (
            <Box className={styles.cardBody}>
              <Box className={classNames(styles.column, styles.mergedSvgs)}>
                <FontAwesomeIcon
                  size="0.75rem"
                  name="circle"
                  sx={{ color: '#4B4B4B' }}
                />
                <Box /> {/* extra line added between svgs */}
                {ended_on ? (
                  <StatusIcon resolved />
                ) : (
                  <StatusIcon resolved={false} />
                )}
              </Box>
              <Box className={styles.column}>
                <Typography>Triggered on</Typography>
                <Typography>
                  {ended_on ? 'Resolved on' : 'Unresolved'}
                </Typography>
              </Box>
              <Box className={styles.column} ml={2} mr={1}>
                <Typography className={styles.date}>
                  {formatDateTimeWithAt(parseDateTimeIgnoringTz(created_on))}
                </Typography>
                <Typography className={styles.date}>
                  {ended_on
                    ? formatDateTimeWithAt(parseDateTimeIgnoringTz(ended_on))
                    : ''}
                </Typography>
              </Box>
              <Box
                className={classNames(
                  styles.column,
                  styles.chip,
                  styles.highlightCol,
                )}
              >
                {alertType}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function NotificationMenu({ alertId }: { alertId: string }) {
  const { navigateTo } = useNotificationProject();
  const { anchorEl, open, onClose, onOpen } = useMenu();

  const redirectAlert = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    navigateTo.editAlert(alertId);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-controls={open ? 'route-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        title="More"
        onClick={onOpen}
      >
        <FontAwesomeIcon name="ellipsis-vertical" />
      </IconButton>
      <Menu
        className={commonStyles.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <MenuItem onClick={redirectAlert} className={commonStyles.menu_item}>
          <ListItemIcon aria-label="edit" title="Edit">
            <FontAwesomeIcon name="square-arrow-up-right" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Edit Source Alert</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

type StatusIconProps = {
  resolved: boolean;
  withLabel?: boolean;
};

export function StatusIcon({ resolved, withLabel = false }: StatusIconProps) {
  const config = {
    icon: resolved ? 'circle-check' : 'circle-exclamation',
    color: resolved ? '#458B4B' : '#895129',
    label: resolved ? 'Resolved' : 'Unresolved',
  };

  return (
    <Box display="flex" alignItems="center">
      <FontAwesomeIcon
        size="0.75rem"
        name={config.icon}
        sx={{ color: config.color }}
      />
      {withLabel && (
        <Typography className={styles.statusLabel} variant="body2">
          {config.label}
        </Typography>
      )}
    </Box>
  );
}
